import React, { useRef, useState, useEffect } from "react";
import * as c from "./lcSearch.module.scss";
import IconSearch from "@icons/iconSearch";
import classNames from "classnames";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch } from "react-instantsearch";
import LcSearchInput from "./lcSearchInput";
import LcSearchResults from "./lcSearchResults";
import { useOnClickOutside } from "@hooks/useOnClickOutside";
import { useLocation } from "@reach/router";

const searchClient = algoliasearch(process.env.GATSBY_ALGOLIA_APP_ID, process.env.GATSBY_ALGOLIA_SEARCH_KEY);

export default function LcSearch({ className }) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const location = useLocation();
  const isProductUpdatesPage = location?.pathname === "/updates/";

  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Use different index for product updates vs learning center
  const indexName = isProductUpdatesPage
    ? process.env.GATSBY_ALGOLIA_CHANGELOG_INDEX
    : process.env.GATSBY_ALGOLIA_SEARCH_INDEX;

  // This ensures that the search input focus is correctly updated
  // if a user clicks the search before react hydrates the page and
  // begins listening to the onFocus/onBlur input events.
  useEffect(() => {
    if (!searchInputRef.current) return;
    const isSearchInputNodeFocused = searchInputRef.current.contains(document.activeElement);
    const isDocumentFocused = document.hasFocus();

    if (isDocumentFocused && isSearchInputNodeFocused) {
      setIsSearchFocused(true);
    }
  }, []);

  useOnClickOutside(searchContainerRef, () => {
    setIsSearchFocused(false);
  });

  // Close search results when navigating to a new page
  useEffect(() => {
    setIsSearchFocused(false);
  }, [location.pathname]);

  return (
    <InstantSearch searchClient={searchClient} indexName={indexName}>
      <section
        role="search"
        className={classNames(c.container, className)}
        ref={searchContainerRef}
        onFocus={() => setIsSearchFocused(true)}
      >
        <IconSearch className={c.icon} />
        <LcSearchInput className={c.searchInput} inputRef={searchInputRef} />
        <LcSearchResults isSearchFocused={isSearchFocused} />
      </section>
    </InstantSearch>
  );
}
